import React, { useState, useEffect, useCallback } from 'react';
import { 
  DollarSign, TrendingUp, Calendar, CreditCard, Lightbulb, ArrowRight, Zap, 
  RefreshCw, AlertTriangle, Clock, Activity, Cpu, ArrowUpRight, ArrowDownRight,
  Target, PieChart, BarChart3, Gauge
} from 'lucide-react';
import api from '../utils/api';

// Color palette for charts
const MODEL_COLORS = ['#00e5ff', '#b24bf3', '#b2ff59', '#ff6b35', '#ffeb3b', '#ec4899'];

const Financials = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.request('/financial/ai-analysis');
      setData(result);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('[Financials] API endpoint not available, using mock data:', err);
      // Use mock data for admin dashboard
      const mockData = {
        currentMonthly: 2847.32,
        lastMonth: 3156.78,
        projectedMonthly: 3200.45,
        todayCost: 94.23,
        percentChange: 1.4,
        costPerRequest: 0.0045,
        costPer1kTokens: 0.0032,
        dailyRequests: 2100,
        monthlyRequests: 63000,
        budget: {
          monthlyBudget: 5000,
          currentSpend: 2847.32,
          remaining: 2152.68,
          projectedSpend: 3200.45,
          utilization: 57,
          status: 'healthy',
          daysRemaining: 12,
          dailyBudget: 166.67,
          dailyAverage: 94.23,
          variance: -72.44,
          varianceStatus: 'under'
        },
        tokenMetrics: {
          inputTokens24h: 1250000,
          outputTokens24h: 312500,
          totalTokens24h: 1562500,
          inputOutputRatio: 4.0
        },
        breakdown: [
          { model: 'Claude Sonnet 3.5', cost: 1423.66, percentage: 50, usageType: 'Text Generation' },
          { model: 'Claude Haiku', cost: 854.20, percentage: 30, usageType: 'Quick Responses' },
          { model: 'Claude Opus', cost: 569.46, percentage: 20, usageType: 'Complex Analysis' }
        ],
        models: {
          current: {
            name: 'Claude Sonnet 3.5',
            inputCostPer1K: 0.003,
            outputCostPer1K: 0.015,
            costPerRequest: 0.0045
          },
          proposed: {
            name: 'Claude Haiku',
            inputCostPer1K: 0.00025,
            outputCostPer1K: 0.00125,
            costPerRequest: 0.0012
          }
        },
        dataSource: 'AWS_Cost_Explorer_and_CloudWatch',
        timestamp: new Date().toISOString()
      };
      setData(mockData);
      setLastUpdated(new Date());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Auto-refresh every 5 minutes
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const formatCurrency = (num, decimals = 2) => {
    if (num === null || num === undefined) return '$--';
    return '$' + num.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '--';
    return num.toLocaleString('en-US');
  };

  const formatLargeNumber = (num) => {
    if (num === null || num === undefined) return '--';
    if (num >= 1e9) return (num / 1e9).toFixed(2) + 'B';
    if (num >= 1e6) return (num / 1e6).toFixed(2) + 'M';
    if (num >= 1e3) return (num / 1e3).toFixed(1) + 'K';
    return num.toLocaleString('en-US');
  };

  // Loading skeleton
  if (loading && !data) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 relative">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-accent opacity-[0.08] blur-[150px] rounded-full pointer-events-none animate-pulse" />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <RefreshCw size={48} className="text-cyan-accent animate-spin mx-auto mb-4" style={{ filter: 'drop-shadow(0 0 20px #00e5ff)' }} />
            <p className="text-gray-400">Loading AI Cost Analytics...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !data) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 relative">
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <AlertTriangle size={48} className="text-orange-accent mx-auto mb-4" style={{ filter: 'drop-shadow(0 0 20px #ff6b35)' }} />
            <p className="text-gray-400 mb-4">Failed to load data: {error}</p>
            <button onClick={fetchData} className="px-4 py-2 bg-cyan-accent/20 text-cyan-accent border border-cyan-accent/40 rounded-lg hover:bg-cyan-accent/30 transition-all">
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const budget = data?.budget || {};
  const tokenMetrics = data?.tokenMetrics || {};
  const breakdown = data?.breakdown || [];
  const models = data?.models || {};

  return (
    <div className="p-4 sm:p-6 lg:p-8 relative">
      {/* Background effects */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-accent opacity-[0.08] blur-[150px] rounded-full pointer-events-none animate-pulse" style={{ animationDuration: '4s' }} />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-accent opacity-[0.06] blur-[150px] rounded-full pointer-events-none animate-pulse" style={{ animationDuration: '5s', animationDelay: '1s' }} />
      
      {/* Header */}
      <div className="flex items-center justify-between mb-6 relative z-10">
        <div>
          <h2 className="text-2xl font-bold text-cyan-accent mb-1 tracking-tight" style={{ textShadow: '0 0 30px #00e5ff, 0 0 60px #00e5ff' }}>
            Admin Dashboard
          </h2>
          <p className="text-sm text-gray-500">AI Cost Analytics & Budget Tracking</p>
        </div>
        <div className="flex items-center gap-3">
          {lastUpdated && (
            <span className="text-xs text-gray-500">
              Updated: {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <button 
            onClick={fetchData} 
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-dark-card border border-dark-border rounded-lg hover:border-cyan-accent/40 transition-all disabled:opacity-50"
          >
            <RefreshCw size={16} className={`text-cyan-accent ${loading ? 'animate-spin' : ''}`} />
            <span className="text-sm text-gray-400">Refresh</span>
          </button>
        </div>
      </div>

      {/* Budget Alert Banner */}
      {budget.status && budget.status !== 'healthy' && (
        <div className={`mb-6 p-4 rounded-lg border relative z-10 ${
          budget.status === 'critical' 
            ? 'bg-red-500/10 border-red-500/40' 
            : 'bg-orange-accent/10 border-orange-accent/40'
        }`} style={{ boxShadow: budget.status === 'critical' ? '0 0 30px rgba(239, 68, 68, 0.2)' : '0 0 30px rgba(255, 107, 53, 0.2)' }}>
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
              budget.status === 'critical' ? 'bg-red-500/20 border border-red-500/40' : 'bg-orange-accent/20 border border-orange-accent/40'
            }`}>
              <AlertTriangle size={24} className={budget.status === 'critical' ? 'text-red-500' : 'text-orange-accent'} style={{ filter: 'drop-shadow(0 0 10px currentColor)' }} />
            </div>
            <div className="flex-1">
              <p className={`text-sm font-bold ${budget.status === 'critical' ? 'text-red-400' : 'text-orange-accent'}`}>
                Budget {budget.status === 'critical' ? 'Critical' : 'Warning'}
              </p>
              <p className={`text-xs ${budget.status === 'critical' ? 'text-red-400/70' : 'text-orange-accent/70'}`}>
                {budget.alert}
              </p>
            </div>
            <div className="text-right">
              <p className={`text-2xl font-bold ${budget.status === 'critical' ? 'text-red-400' : 'text-orange-accent'}`} style={{ textShadow: budget.status === 'critical' ? '0 0 20px #ef4444' : '0 0 20px #ff6b35' }}>
                {formatCurrency(budget.projectedSpend - budget.monthlyBudget, 0)}
              </p>
              <p className={`text-xs ${budget.status === 'critical' ? 'text-red-400/60' : 'text-orange-accent/60'}`}>projected overage</p>
            </div>
          </div>
        </div>
      )}

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 relative z-10 mb-6">
        {/* Current Monthly Spend */}
        <div className="bg-dark-card border border-dark-border rounded-lg p-5 relative overflow-hidden group hover:border-cyan-accent/40 transition-all"
             style={{ background: '#141414', boxShadow: '0 0 30px rgba(0, 0, 0, 0.5), 0 0 2px rgba(0, 229, 255, 0.2)' }}>
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-accent/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          <div className="flex items-center justify-between mb-3 relative z-10">
            <div className="w-10 h-10 rounded-lg bg-cyan-accent/10 flex items-center justify-center border border-cyan-accent/30" style={{ boxShadow: '0 0 20px rgba(0, 229, 255, 0.2)' }}>
              <DollarSign size={20} className="text-cyan-accent" style={{ filter: 'drop-shadow(0 0 8px currentColor)' }} />
            </div>
            <span className="text-xs px-2 py-1 rounded bg-cyan-accent/10 text-cyan-accent border border-cyan-accent/30">
              {budget.daysRemaining} days left
            </span>
          </div>
          <p className="text-sm text-gray-500 mb-1">Current Month Spend</p>
          <p className="text-2xl font-bold text-cyan-accent" style={{ textShadow: '0 0 20px #00e5ff' }}>
            {formatCurrency(data?.currentMonthly, 0)}
          </p>
          <div className="flex items-center gap-1 mt-2">
            <span className="text-xs text-gray-500">vs last month:</span>
            <span className={`text-xs font-medium ${data?.currentMonthly < data?.lastMonth ? 'text-lime-accent' : 'text-orange-accent'}`}>
              {formatCurrency(data?.lastMonth, 0)}
            </span>
          </div>
        </div>

        {/* Projected Monthly */}
        <div className="bg-dark-card border border-dark-border rounded-lg p-5 relative overflow-hidden group hover:border-purple-accent/40 transition-all"
             style={{ background: '#141414', boxShadow: '0 0 30px rgba(0, 0, 0, 0.5), 0 0 2px rgba(178, 75, 243, 0.2)' }}>
          <div className="absolute inset-0 bg-gradient-to-br from-purple-accent/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          <div className="flex items-center justify-between mb-3 relative z-10">
            <div className="w-10 h-10 rounded-lg bg-purple-accent/10 flex items-center justify-center border border-purple-accent/30" style={{ boxShadow: '0 0 20px rgba(178, 75, 243, 0.2)' }}>
              <TrendingUp size={20} className="text-purple-accent" style={{ filter: 'drop-shadow(0 0 8px currentColor)' }} />
            </div>
            <span className={`text-xs px-2 py-1 rounded border ${
              data?.projectedMonthly > budget.monthlyBudget 
                ? 'bg-red-500/10 text-red-400 border-red-500/30' 
                : 'bg-lime-accent/10 text-lime-accent border-lime-accent/30'
            }`}>
              {data?.projectedMonthly > budget.monthlyBudget ? 'Over Budget' : 'On Track'}
            </span>
          </div>
          <p className="text-sm text-gray-500 mb-1">Projected Monthly</p>
          <p className={`text-2xl font-bold ${data?.projectedMonthly > budget.monthlyBudget ? 'text-red-400' : 'text-purple-accent'}`} 
             style={{ textShadow: data?.projectedMonthly > budget.monthlyBudget ? '0 0 20px #ef4444' : '0 0 20px #b24bf3' }}>
            {formatCurrency(data?.projectedMonthly, 0)}
          </p>
          <div className="flex items-center gap-1 mt-2">
            {data?.percentChange > 0 ? (
              <ArrowUpRight size={14} className="text-red-400" />
            ) : (
              <ArrowDownRight size={14} className="text-lime-accent" />
            )}
            <span className={`text-xs font-medium ${data?.percentChange > 0 ? 'text-red-400' : 'text-lime-accent'}`}>
              {data?.percentChange > 0 ? '+' : ''}{data?.percentChange?.toFixed(1)}% change
            </span>
          </div>
        </div>

        {/* Today's Cost */}
        <div className="bg-dark-card border border-dark-border rounded-lg p-5 relative overflow-hidden group hover:border-lime-accent/40 transition-all"
             style={{ background: '#141414', boxShadow: '0 0 30px rgba(0, 0, 0, 0.5), 0 0 2px rgba(178, 255, 89, 0.2)' }}>
          <div className="absolute inset-0 bg-gradient-to-br from-lime-accent/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          <div className="flex items-center justify-between mb-3 relative z-10">
            <div className="w-10 h-10 rounded-lg bg-lime-accent/10 flex items-center justify-center border border-lime-accent/30" style={{ boxShadow: '0 0 20px rgba(178, 255, 89, 0.2)' }}>
              <Clock size={20} className="text-lime-accent" style={{ filter: 'drop-shadow(0 0 8px currentColor)' }} />
            </div>
            <span className="text-xs px-2 py-1 rounded bg-lime-accent/10 text-lime-accent border border-lime-accent/30 animate-pulse">
              Live
            </span>
          </div>
          <p className="text-sm text-gray-500 mb-1">Today's Cost</p>
          <p className="text-2xl font-bold text-lime-accent" style={{ textShadow: '0 0 20px #b2ff59' }}>
            {formatCurrency(data?.todayCost, 2)}
          </p>
          <div className="flex items-center gap-1 mt-2">
            <span className="text-xs text-gray-500">Daily budget:</span>
            <span className="text-xs font-medium text-gray-400">{formatCurrency(budget.dailyBudget, 2)}</span>
          </div>
        </div>

        {/* Cost Efficiency */}
        <div className="bg-dark-card border border-dark-border rounded-lg p-5 relative overflow-hidden group hover:border-orange-accent/40 transition-all"
             style={{ background: '#141414', boxShadow: '0 0 30px rgba(0, 0, 0, 0.5), 0 0 2px rgba(255, 107, 53, 0.2)' }}>
          <div className="absolute inset-0 bg-gradient-to-br from-orange-accent/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          <div className="flex items-center justify-between mb-3 relative z-10">
            <div className="w-10 h-10 rounded-lg bg-orange-accent/10 flex items-center justify-center border border-orange-accent/30" style={{ boxShadow: '0 0 20px rgba(255, 107, 53, 0.2)' }}>
              <Activity size={20} className="text-orange-accent" style={{ filter: 'drop-shadow(0 0 8px currentColor)' }} />
            </div>
            <span className="text-xs px-2 py-1 rounded bg-orange-accent/10 text-orange-accent border border-orange-accent/30">
              Per Request
            </span>
          </div>
          <p className="text-sm text-gray-500 mb-1">Cost per Request</p>
          <p className="text-2xl font-bold text-orange-accent" style={{ textShadow: '0 0 20px #ff6b35' }}>
            {formatCurrency(data?.costPerRequest, 4)}
          </p>
          <div className="flex items-center gap-1 mt-2">
            <span className="text-xs text-gray-500">Per 1K tokens:</span>
            <span className="text-xs font-medium text-gray-400">{formatCurrency(data?.costPer1kTokens, 4)}</span>
          </div>
        </div>
      </div>

      {/* Budget Gauge & Model Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6 relative z-10">
        {/* Budget Utilization Gauge */}
        <div className="bg-dark-card border border-dark-border rounded-lg p-6 relative overflow-hidden group hover:border-cyan-accent/40 transition-all"
             style={{ background: '#141414', boxShadow: '0 0 30px rgba(0, 0, 0, 0.5), 0 0 2px rgba(0, 229, 255, 0.2)' }}>
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-accent/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          
          <div className="flex items-center justify-between mb-6 relative z-10">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-lg bg-cyan-accent/10 flex items-center justify-center border border-cyan-accent/30" style={{ boxShadow: '0 0 20px rgba(0, 229, 255, 0.2)' }}>
                <Gauge size={20} className="text-cyan-accent" style={{ filter: 'drop-shadow(0 0 8px currentColor)' }} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white" style={{ textShadow: '0 0 10px rgba(255,255,255,0.3)' }}>Budget Utilization</h3>
                <p className="text-xs text-gray-500">Monthly budget tracking</p>
              </div>
            </div>
            <span className={`text-xs px-3 py-1 rounded-full font-semibold ${
              budget.status === 'healthy' ? 'bg-lime-accent/20 text-lime-accent border border-lime-accent/40' :
              budget.status === 'warning' ? 'bg-orange-accent/20 text-orange-accent border border-orange-accent/40' :
              'bg-red-500/20 text-red-400 border border-red-500/40'
            }`}>
              {budget.status?.charAt(0).toUpperCase() + budget.status?.slice(1)}
            </span>
          </div>

          {/* Circular Gauge */}
          <div className="flex justify-center mb-6 relative z-10">
            <div className="relative w-48 h-48">
              <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="40" fill="none" stroke="#2a2a2a" strokeWidth="8" />
                <circle 
                  cx="50" cy="50" r="40" fill="none" 
                  stroke={budget.utilization > 90 ? '#ef4444' : budget.utilization > 75 ? '#ff6b35' : '#00e5ff'}
                  strokeWidth="8" strokeLinecap="round"
                  strokeDasharray={`${(budget.utilization / 100) * 251.2} 251.2`}
                  style={{ 
                    filter: `drop-shadow(0 0 10px ${budget.utilization > 90 ? '#ef4444' : budget.utilization > 75 ? '#ff6b35' : '#00e5ff'})`,
                    transition: 'stroke-dasharray 1s ease-out'
                  }}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <p className={`text-4xl font-bold ${
                  budget.utilization > 90 ? 'text-red-400' : budget.utilization > 75 ? 'text-orange-accent' : 'text-cyan-accent'
                }`} style={{ textShadow: `0 0 20px ${budget.utilization > 90 ? '#ef4444' : budget.utilization > 75 ? '#ff6b35' : '#00e5ff'}` }}>
                  {budget.utilization}%
                </p>
                <p className="text-xs text-gray-500">utilized</p>
              </div>
            </div>
          </div>

          {/* Budget Details */}
          <div className="space-y-3 relative z-10">
            <div className="flex items-center justify-between p-3 rounded-lg bg-dark-border/30 border border-dark-border/50">
              <span className="text-sm text-gray-400">Monthly Budget</span>
              <span className="text-sm font-bold text-white">{formatCurrency(budget.monthlyBudget, 0)}</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-dark-border/30 border border-dark-border/50">
              <span className="text-sm text-gray-400">Current Spend</span>
              <span className="text-sm font-bold text-cyan-accent">{formatCurrency(budget.currentSpend, 0)}</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-dark-border/30 border border-dark-border/50">
              <span className="text-sm text-gray-400">Remaining</span>
              <span className="text-sm font-bold text-lime-accent">{formatCurrency(budget.remaining, 0)}</span>
            </div>
            <div className={`flex items-center justify-between p-3 rounded-lg border ${
              budget.projectedSpend > budget.monthlyBudget 
                ? 'bg-red-500/10 border-red-500/30' 
                : 'bg-orange-accent/10 border-orange-accent/30'
            }`}>
              <span className={`text-sm ${budget.projectedSpend > budget.monthlyBudget ? 'text-red-400' : 'text-orange-accent'}`}>Projected Spend</span>
              <span className={`text-sm font-bold ${budget.projectedSpend > budget.monthlyBudget ? 'text-red-400' : 'text-orange-accent'}`}>
                {formatCurrency(budget.projectedSpend, 0)}
              </span>
            </div>
          </div>
        </div>

        {/* Model Cost Breakdown - Donut Chart */}
        <div className="bg-dark-card border border-dark-border rounded-lg p-6 relative overflow-hidden group hover:border-purple-accent/40 transition-all"
             style={{ background: '#141414', boxShadow: '0 0 30px rgba(0, 0, 0, 0.5), 0 0 2px rgba(178, 75, 243, 0.2)' }}>
          <div className="absolute inset-0 bg-gradient-to-br from-purple-accent/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          
          <div className="flex items-center space-x-3 mb-6 relative z-10">
            <div className="w-10 h-10 rounded-lg bg-purple-accent/10 flex items-center justify-center border border-purple-accent/30" style={{ boxShadow: '0 0 20px rgba(178, 75, 243, 0.2)' }}>
              <PieChart size={20} className="text-purple-accent" style={{ filter: 'drop-shadow(0 0 8px currentColor)' }} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white" style={{ textShadow: '0 0 10px rgba(255,255,255,0.3)' }}>Model Cost Breakdown</h3>
              <p className="text-xs text-gray-500">Spend by AI model</p>
            </div>
          </div>

          {/* Donut Chart */}
          <div className="flex justify-center mb-6 relative z-10">
            <div className="relative w-48 h-48">
              <svg className="w-full h-full" viewBox="0 0 100 100">
                {breakdown.map((item, index) => {
                  const prevPercent = breakdown.slice(0, index).reduce((sum, b) => sum + b.percentage, 0);
                  const dashArray = (item.percentage / 100) * 251.2;
                  const dashOffset = -(prevPercent / 100) * 251.2;
                  return (
                    <circle 
                      key={index}
                      cx="50" cy="50" r="40" fill="none" 
                      stroke={MODEL_COLORS[index % MODEL_COLORS.length]}
                      strokeWidth="12"
                      strokeDasharray={`${dashArray} 251.2`}
                      strokeDashoffset={dashOffset}
                      transform="rotate(-90 50 50)"
                      style={{ filter: `drop-shadow(0 0 5px ${MODEL_COLORS[index % MODEL_COLORS.length]})` }}
                    />
                  );
                })}
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <p className="text-2xl font-bold text-white" style={{ textShadow: '0 0 15px rgba(255,255,255,0.3)' }}>
                  {formatCurrency(breakdown.reduce((sum, m) => sum + m.cost, 0), 0)}
                </p>
                <p className="text-xs text-gray-500">total</p>
              </div>
            </div>
          </div>

          {/* Model Legend */}
          <div className="space-y-2 relative z-10">
            {breakdown.map((model, index) => (
              <div key={index} className="flex items-center justify-between p-2 rounded-lg bg-dark-border/30 border border-dark-border/50 hover:border-purple-accent/30 transition-all">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: MODEL_COLORS[index % MODEL_COLORS.length], boxShadow: `0 0 8px ${MODEL_COLORS[index % MODEL_COLORS.length]}` }} />
                  <div>
                    <p className="text-sm font-medium text-white">{model.model}</p>
                    <p className="text-xs text-gray-500">{model.usageType}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-white">{formatCurrency(model.cost, 2)}</p>
                  <p className="text-xs text-gray-500">{model.percentage.toFixed(1)}%</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Token Metrics & Request Volume */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6 relative z-10">
        {/* Token Usage (24h) */}
        <div className="bg-dark-card border border-dark-border rounded-lg p-6 relative overflow-hidden group hover:border-lime-accent/40 transition-all"
             style={{ background: '#141414', boxShadow: '0 0 30px rgba(0, 0, 0, 0.5), 0 0 2px rgba(178, 255, 89, 0.2)' }}>
          <div className="absolute inset-0 bg-gradient-to-br from-lime-accent/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          
          <div className="flex items-center justify-between mb-6 relative z-10">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-lg bg-lime-accent/10 flex items-center justify-center border border-lime-accent/30" style={{ boxShadow: '0 0 20px rgba(178, 255, 89, 0.2)' }}>
                <Cpu size={20} className="text-lime-accent" style={{ filter: 'drop-shadow(0 0 8px currentColor)' }} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white" style={{ textShadow: '0 0 10px rgba(255,255,255,0.3)' }}>Token Usage (24h)</h3>
                <p className="text-xs text-gray-500">Input/Output token analysis</p>
              </div>
            </div>
            <span className="text-xs px-2 py-1 rounded bg-lime-accent/10 text-lime-accent border border-lime-accent/30 animate-pulse">Live</span>
          </div>

          <div className="space-y-4 relative z-10">
            {/* Input Tokens */}
            <div className="p-4 rounded-lg bg-cyan-accent/5 border border-cyan-accent/20">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <ArrowDownRight size={16} className="text-cyan-accent" />
                  <span className="text-sm text-gray-400">Input Tokens</span>
                </div>
                <span className="text-xs text-gray-500">Last 24h</span>
              </div>
              <p className="text-2xl font-bold text-cyan-accent" style={{ textShadow: '0 0 15px #00e5ff' }}>
                {formatLargeNumber(tokenMetrics.inputTokens24h)}
              </p>
            </div>

            {/* Output Tokens */}
            <div className="p-4 rounded-lg bg-purple-accent/5 border border-purple-accent/20">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <ArrowUpRight size={16} className="text-purple-accent" />
                  <span className="text-sm text-gray-400">Output Tokens</span>
                </div>
                <span className="text-xs text-gray-500">Last 24h</span>
              </div>
              <p className="text-2xl font-bold text-purple-accent" style={{ textShadow: '0 0 15px #b24bf3' }}>
                {formatLargeNumber(tokenMetrics.outputTokens24h)}
              </p>
            </div>

            {/* Total & Ratio */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 rounded-lg bg-dark-border/30 border border-dark-border/50 text-center">
                <p className="text-xs text-gray-500 mb-1">Total Tokens</p>
                <p className="text-lg font-bold text-white">{formatLargeNumber(tokenMetrics.totalTokens24h)}</p>
              </div>
              <div className="p-3 rounded-lg bg-dark-border/30 border border-dark-border/50 text-center">
                <p className="text-xs text-gray-500 mb-1">I/O Ratio</p>
                <p className="text-lg font-bold text-lime-accent" style={{ textShadow: '0 0 10px #b2ff59' }}>
                  {tokenMetrics.inputOutputRatio?.toFixed(1)}:1
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Request Volume */}
        <div className="bg-dark-card border border-dark-border rounded-lg p-6 relative overflow-hidden group hover:border-orange-accent/40 transition-all"
             style={{ background: '#141414', boxShadow: '0 0 30px rgba(0, 0, 0, 0.5), 0 0 2px rgba(255, 107, 53, 0.2)' }}>
          <div className="absolute inset-0 bg-gradient-to-br from-orange-accent/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          
          <div className="flex items-center space-x-3 mb-6 relative z-10">
            <div className="w-10 h-10 rounded-lg bg-orange-accent/10 flex items-center justify-center border border-orange-accent/30" style={{ boxShadow: '0 0 20px rgba(255, 107, 53, 0.2)' }}>
              <BarChart3 size={20} className="text-orange-accent" style={{ filter: 'drop-shadow(0 0 8px currentColor)' }} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white" style={{ textShadow: '0 0 10px rgba(255,255,255,0.3)' }}>Request Volume</h3>
              <p className="text-xs text-gray-500">API usage metrics</p>
            </div>
          </div>

          <div className="space-y-4 relative z-10">
            <div className="flex items-center justify-between p-4 rounded-lg bg-dark-border/30 border border-dark-border/50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-cyan-accent/10 flex items-center justify-center border border-cyan-accent/30">
                  <Clock size={18} className="text-cyan-accent" />
                </div>
                <div>
                  <p className="text-sm font-medium text-white">Daily Requests</p>
                  <p className="text-xs text-gray-500">Average per day</p>
                </div>
              </div>
              <p className="text-xl font-bold text-cyan-accent" style={{ textShadow: '0 0 15px #00e5ff' }}>
                {formatNumber(data?.dailyRequests)}
              </p>
            </div>

            <div className="flex items-center justify-between p-4 rounded-lg bg-dark-border/30 border border-dark-border/50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-accent/10 flex items-center justify-center border border-purple-accent/30">
                  <Calendar size={18} className="text-purple-accent" />
                </div>
                <div>
                  <p className="text-sm font-medium text-white">Monthly Requests</p>
                  <p className="text-xs text-gray-500">Current month</p>
                </div>
              </div>
              <p className="text-xl font-bold text-purple-accent" style={{ textShadow: '0 0 15px #b24bf3' }}>
                {formatNumber(data?.monthlyRequests)}
              </p>
            </div>

            <div className={`flex items-center justify-between p-4 rounded-lg border ${
              budget.varianceStatus === 'over' 
                ? 'bg-red-500/10 border-red-500/30' 
                : 'bg-lime-accent/10 border-lime-accent/30'
            }`}>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${
                  budget.varianceStatus === 'over' 
                    ? 'bg-red-500/10 border-red-500/30' 
                    : 'bg-lime-accent/10 border-lime-accent/30'
                }`}>
                  <TrendingUp size={18} className={budget.varianceStatus === 'over' ? 'text-red-400' : 'text-lime-accent'} />
                </div>
                <div>
                  <p className="text-sm font-medium text-white">Daily Average Spend</p>
                  <p className="text-xs text-gray-500">
                    {budget.varianceStatus === 'over' ? '+' : '-'}{formatCurrency(Math.abs(budget.variance), 2)} vs budget
                  </p>
                </div>
              </div>
              <p className={`text-xl font-bold ${budget.varianceStatus === 'over' ? 'text-red-400' : 'text-lime-accent'}`} 
                 style={{ textShadow: budget.varianceStatus === 'over' ? '0 0 15px #ef4444' : '0 0 15px #b2ff59' }}>
                {formatCurrency(budget.dailyAverage, 2)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Model Comparison & Recommendations */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 relative z-10">
        {/* Model Comparison */}
        <div className="bg-dark-card border border-dark-border rounded-lg p-6 relative overflow-hidden group hover:border-purple-accent/40 transition-all"
             style={{ background: '#141414', boxShadow: '0 0 30px rgba(0, 0, 0, 0.5), 0 0 2px rgba(178, 75, 243, 0.2)' }}>
          <div className="absolute inset-0 bg-gradient-to-br from-purple-accent/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          
          <div className="flex items-center space-x-3 mb-6 relative z-10">
            <div className="w-10 h-10 rounded-lg bg-purple-accent/10 flex items-center justify-center border border-purple-accent/30" style={{ boxShadow: '0 0 20px rgba(178, 75, 243, 0.2)' }}>
              <TrendingUp size={20} className="text-purple-accent" style={{ filter: 'drop-shadow(0 0 8px currentColor)' }} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white" style={{ textShadow: '0 0 10px rgba(255,255,255,0.3)' }}>Model Comparison</h3>
              <p className="text-xs text-gray-500">Current vs proposed optimization</p>
            </div>
          </div>

          <div className="space-y-4 relative z-10">
            {/* Model Names */}
            <div className="flex items-center justify-between p-4 rounded-lg border border-purple-accent/30 bg-gradient-to-r from-cyan-accent/10 via-purple-accent/10 to-lime-accent/10"
                 style={{ boxShadow: '0 0 20px rgba(178, 75, 243, 0.2), inset 0 0 20px rgba(178, 75, 243, 0.1)' }}>
              <div className="flex-1 text-center">
                <p className="text-xs text-gray-400 mb-1">Current</p>
                <p className="text-lg font-bold text-cyan-accent" style={{ textShadow: '0 0 15px #00e5ff' }}>
                  {models.current?.name || 'Claude Sonnet 4'}
                </p>
              </div>
              <ArrowRight size={24} className="text-purple-accent mx-4 animate-pulse" style={{ filter: 'drop-shadow(0 0 10px currentColor)' }} />
              <div className="flex-1 text-center">
                <p className="text-xs text-gray-400 mb-1">Proposed</p>
                <p className="text-lg font-bold text-lime-accent" style={{ textShadow: '0 0 15px #b2ff59' }}>
                  {models.proposed?.name || 'Claude Haiku'}
                </p>
              </div>
            </div>

            {/* Metrics Cards */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 rounded-lg border border-cyan-accent/30 bg-cyan-accent/5 hover:bg-cyan-accent/10 transition-all" style={{ boxShadow: '0 0 15px rgba(0, 229, 255, 0.2)' }}>
                <p className="text-xs text-gray-400 mb-1">Input Cost</p>
                <p className="text-sm font-bold text-cyan-accent" style={{ textShadow: '0 0 10px #00e5ff' }}>
                  {formatCurrency(models.current?.inputCostPer1K, 4)}
                </p>
                <p className="text-xs text-gray-500">per 1K tokens</p>
              </div>
              <div className="p-3 rounded-lg border border-lime-accent/30 bg-lime-accent/5 hover:bg-lime-accent/10 transition-all" style={{ boxShadow: '0 0 15px rgba(178, 255, 89, 0.2)' }}>
                <p className="text-xs text-gray-400 mb-1">Input Cost</p>
                <p className="text-sm font-bold text-lime-accent" style={{ textShadow: '0 0 10px #b2ff59' }}>
                  {formatCurrency(models.proposed?.inputCostPer1K, 4)}
                </p>
                <p className="text-xs text-orange-accent font-bold">
                  -{((1 - models.proposed?.inputCostPer1K / models.current?.inputCostPer1K) * 100).toFixed(0)}%
                </p>
              </div>
              <div className="p-3 rounded-lg border border-cyan-accent/30 bg-cyan-accent/5 hover:bg-cyan-accent/10 transition-all" style={{ boxShadow: '0 0 15px rgba(0, 229, 255, 0.2)' }}>
                <p className="text-xs text-gray-400 mb-1">Output Cost</p>
                <p className="text-sm font-bold text-cyan-accent" style={{ textShadow: '0 0 10px #00e5ff' }}>
                  {formatCurrency(models.current?.outputCostPer1K, 4)}
                </p>
                <p className="text-xs text-gray-500">per 1K tokens</p>
              </div>
              <div className="p-3 rounded-lg border border-lime-accent/30 bg-lime-accent/5 hover:bg-lime-accent/10 transition-all" style={{ boxShadow: '0 0 15px rgba(178, 255, 89, 0.2)' }}>
                <p className="text-xs text-gray-400 mb-1">Output Cost</p>
                <p className="text-sm font-bold text-lime-accent" style={{ textShadow: '0 0 10px #b2ff59' }}>
                  {formatCurrency(models.proposed?.outputCostPer1K, 4)}
                </p>
                <p className="text-xs text-orange-accent font-bold">
                  -{((1 - models.proposed?.outputCostPer1K / models.current?.outputCostPer1K) * 100).toFixed(0)}%
                </p>
              </div>
            </div>

            {/* Final Comparison */}
            <div className="p-4 rounded-lg border-2 border-purple-accent/40 bg-gradient-to-br from-purple-accent/10 to-transparent"
                 style={{ boxShadow: '0 0 25px rgba(178, 75, 243, 0.3), inset 0 0 25px rgba(178, 75, 243, 0.1)' }}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex-1">
                  <p className="text-xs text-gray-400">Current Cost/Request</p>
                  <p className="text-xl font-bold text-cyan-accent" style={{ textShadow: '0 0 20px #00e5ff' }}>
                    {formatCurrency(models.current?.costPerRequest, 4)}
                  </p>
                </div>
                <Zap size={28} className="text-yellow-accent mx-4 animate-pulse" style={{ filter: 'drop-shadow(0 0 15px currentColor)', animationDuration: '1.5s' }} />
                <div className="flex-1 text-right">
                  <p className="text-xs text-gray-400">Proposed Cost/Request</p>
                  <p className="text-xl font-bold text-lime-accent" style={{ textShadow: '0 0 20px #b2ff59' }}>
                    {formatCurrency(models.proposed?.costPerRequest, 4)}
                  </p>
                </div>
              </div>
              <div className="text-center pt-2 border-t border-purple-accent/30">
                <p className="text-2xl font-bold text-orange-accent" style={{ textShadow: '0 0 25px #ff6b35' }}>
                  {((1 - models.proposed?.costPerRequest / models.current?.costPerRequest) * 100).toFixed(0)}% SAVINGS
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* AI-Powered Recommendations */}
        <div className="bg-dark-card border border-dark-border rounded-lg p-6 relative overflow-hidden group hover:border-orange-accent/40 transition-all"
             style={{ background: '#141414', boxShadow: '0 0 30px rgba(0, 0, 0, 0.5), 0 0 2px rgba(255, 107, 53, 0.2)' }}>
          <div className="absolute inset-0 bg-gradient-to-br from-orange-accent/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          
          <div className="flex items-center space-x-3 mb-6 relative z-10">
            <div className="w-10 h-10 rounded-lg bg-orange-accent/10 flex items-center justify-center border border-orange-accent/30" style={{ boxShadow: '0 0 20px rgba(255, 107, 53, 0.2)' }}>
              <Lightbulb size={20} className="text-orange-accent" style={{ filter: 'drop-shadow(0 0 8px currentColor)' }} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white" style={{ textShadow: '0 0 10px rgba(255,255,255,0.3)' }}>AI-Powered Recommendations</h3>
              <p className="text-xs text-gray-500">Optimization opportunities</p>
            </div>
          </div>

          <div className="space-y-4 relative z-10">
            <div className="border border-dark-border/50 rounded-lg p-4 hover:border-lime-accent/40 transition-all hover:bg-lime-accent/5">
              <div className="flex items-start justify-between mb-2">
                <h4 className="text-sm font-bold text-white">Switch to {models.proposed?.name || 'Claude Haiku'}</h4>
                <span className="text-xs px-2 py-1 rounded bg-orange-accent/20 text-orange-accent border border-orange-accent/40" style={{ boxShadow: '0 0 10px rgba(255, 107, 53, 0.2)' }}>High Impact</span>
              </div>
              <p className="text-xs text-gray-400 mb-3">
                Migrate to {models.proposed?.name || 'Claude Haiku'} for {((1 - models.proposed?.costPerRequest / models.current?.costPerRequest) * 100).toFixed(0)}% cost reduction
              </p>
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold text-lime-accent" style={{ textShadow: '0 0 10px #b2ff59' }}>
                  Saves ~{formatCurrency((data?.projectedMonthly || 0) * (1 - models.proposed?.costPerRequest / models.current?.costPerRequest), 0)}/mo
                </span>
                <span className="text-xs text-gray-500">Effort: Medium</span>
              </div>
            </div>

            <div className="border border-dark-border/50 rounded-lg p-4 hover:border-cyan-accent/40 transition-all hover:bg-cyan-accent/5">
              <div className="flex items-start justify-between mb-2">
                <h4 className="text-sm font-bold text-white">Enable Prompt Caching</h4>
                <span className="text-xs px-2 py-1 rounded bg-yellow-accent/20 text-yellow-accent border border-yellow-accent/40" style={{ boxShadow: '0 0 10px rgba(255, 235, 59, 0.2)' }}>Medium Impact</span>
              </div>
              <p className="text-xs text-gray-400 mb-3">Cache system prompts to reduce token usage by up to 90%</p>
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold text-lime-accent" style={{ textShadow: '0 0 10px #b2ff59' }}>
                  Saves ~{formatCurrency((data?.projectedMonthly || 0) * 0.15, 0)}/mo
                </span>
                <span className="text-xs text-gray-500">Effort: Low</span>
              </div>
            </div>

            <div className="border border-dark-border/50 rounded-lg p-4 hover:border-purple-accent/40 transition-all hover:bg-purple-accent/5">
              <div className="flex items-start justify-between mb-2">
                <h4 className="text-sm font-bold text-white">Optimize Token Usage</h4>
                <span className="text-xs px-2 py-1 rounded bg-yellow-accent/20 text-yellow-accent border border-yellow-accent/40" style={{ boxShadow: '0 0 10px rgba(255, 235, 59, 0.2)' }}>Medium Impact</span>
              </div>
              <p className="text-xs text-gray-400 mb-3">Reduce prompt length and few-shot examples</p>
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold text-lime-accent" style={{ textShadow: '0 0 10px #b2ff59' }}>
                  Saves ~{formatCurrency((data?.projectedMonthly || 0) * 0.1, 0)}/mo
                </span>
                <span className="text-xs text-gray-500">Effort: Medium</span>
              </div>
            </div>
          </div>
        </div>
      </div>


      {/* Industry Benchmarks */}
      <div className="mt-6 relative z-10">
        <div className="bg-dark-card border border-dark-border rounded-lg p-6 relative overflow-hidden group hover:border-yellow-accent/40 transition-all"
             style={{ background: '#141414', boxShadow: '0 0 30px rgba(0, 0, 0, 0.5), 0 0 2px rgba(255, 235, 59, 0.2)' }}>
          <div className="absolute inset-0 bg-gradient-to-br from-yellow-accent/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          
          <div className="flex items-center justify-between mb-6 relative z-10">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-accent/10 flex items-center justify-center border border-yellow-accent/30" style={{ boxShadow: '0 0 20px rgba(255, 235, 59, 0.2)' }}>
                <Target size={20} className="text-yellow-accent" style={{ filter: 'drop-shadow(0 0 8px currentColor)' }} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white" style={{ textShadow: '0 0 10px rgba(255,255,255,0.3)' }}>Industry Benchmarks</h3>
                <p className="text-xs text-gray-500">How you compare to typical LLM usage</p>
              </div>
            </div>
            <span className="text-xs px-2 py-1 rounded bg-lime-accent/10 text-lime-accent border border-lime-accent/30">2024 Data</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 relative z-10">
            {/* Input:Output Ratio */}
            <div className="p-4 rounded-lg bg-dark-border/30 border border-dark-border/50">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-semibold text-white">Input:Output Ratio</p>
                <span className={`text-xs px-2 py-1 rounded ${
                  tokenMetrics.inputOutputRatio >= 3 && tokenMetrics.inputOutputRatio <= 5 
                    ? 'bg-lime-accent/20 text-lime-accent border border-lime-accent/40' 
                    : tokenMetrics.inputOutputRatio < 3 
                      ? 'bg-cyan-accent/20 text-cyan-accent border border-cyan-accent/40'
                      : 'bg-orange-accent/20 text-orange-accent border border-orange-accent/40'
                }`}>
                  {tokenMetrics.inputOutputRatio >= 3 && tokenMetrics.inputOutputRatio <= 5 ? 'Optimal' : tokenMetrics.inputOutputRatio < 3 ? 'Low Input' : 'High Input'}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs mb-2">
                <span className="text-gray-500">Industry avg: 3:1 to 5:1</span>
                <span className="text-white font-medium">Your ratio: {tokenMetrics.inputOutputRatio?.toFixed(1)}:1</span>
              </div>
              <div className="w-full bg-dark-border/50 rounded-full h-2">
                <div 
                  className="h-2 rounded-full transition-all duration-500"
                  style={{ 
                    width: `${Math.min((tokenMetrics.inputOutputRatio / 7) * 100, 100)}%`,
                    background: tokenMetrics.inputOutputRatio >= 3 && tokenMetrics.inputOutputRatio <= 5 ? '#b2ff59' : tokenMetrics.inputOutputRatio < 3 ? '#00e5ff' : '#ff6b35',
                    boxShadow: `0 0 10px ${tokenMetrics.inputOutputRatio >= 3 && tokenMetrics.inputOutputRatio <= 5 ? '#b2ff59' : tokenMetrics.inputOutputRatio < 3 ? '#00e5ff' : '#ff6b35'}`
                  }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">💡 Typical enterprise apps have 3-5x more input than output</p>
            </div>

            {/* Cost Efficiency */}
            <div className="p-4 rounded-lg bg-dark-border/30 border border-dark-border/50">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-semibold text-white">Cost Efficiency</p>
                <span className={`text-xs px-2 py-1 rounded ${
                  data?.costPer1kTokens <= 0.003 
                    ? 'bg-lime-accent/20 text-lime-accent border border-lime-accent/40' 
                    : data?.costPer1kTokens <= 0.006 
                      ? 'bg-cyan-accent/20 text-cyan-accent border border-cyan-accent/40'
                      : 'bg-orange-accent/20 text-orange-accent border border-orange-accent/40'
                }`}>
                  {data?.costPer1kTokens <= 0.003 ? 'Excellent' : data?.costPer1kTokens <= 0.006 ? 'Good' : 'Above Avg'}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs mb-2">
                <span className="text-gray-500">Industry avg: $0.002-$0.008/1K</span>
                <span className="text-white font-medium">Your cost: {formatCurrency(data?.costPer1kTokens, 4)}</span>
              </div>
              <div className="w-full bg-dark-border/50 rounded-full h-2">
                <div 
                  className="h-2 rounded-full transition-all duration-500"
                  style={{ 
                    width: `${Math.min((data?.costPer1kTokens / 0.01) * 100, 100)}%`,
                    background: data?.costPer1kTokens <= 0.003 ? '#b2ff59' : data?.costPer1kTokens <= 0.006 ? '#00e5ff' : '#ff6b35',
                    boxShadow: `0 0 10px ${data?.costPer1kTokens <= 0.003 ? '#b2ff59' : data?.costPer1kTokens <= 0.006 ? '#00e5ff' : '#ff6b35'}`
                  }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">💡 Blended rate depends on model mix</p>
            </div>

            {/* Tokens per Request */}
            <div className="p-4 rounded-lg bg-dark-border/30 border border-dark-border/50">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-semibold text-white">Tokens per Request</p>
                {(() => {
                  const tokensPerReq = data?.dailyRequests ? Math.round(tokenMetrics.totalTokens24h / data.dailyRequests) : 0;
                  return (
                    <span className={`text-xs px-2 py-1 rounded ${
                      tokensPerReq < 1500 
                        ? 'bg-lime-accent/20 text-lime-accent border border-lime-accent/40' 
                        : tokensPerReq < 4000 
                          ? 'bg-cyan-accent/20 text-cyan-accent border border-cyan-accent/40'
                          : 'bg-orange-accent/20 text-orange-accent border border-orange-accent/40'
                    }`}>
                      {tokensPerReq < 1500 ? 'Light' : tokensPerReq < 4000 ? 'Moderate' : 'Heavy'}
                    </span>
                  );
                })()}
              </div>
              <div className="text-xs text-gray-500 mb-2">
                <p>Industry ranges by use case:</p>
                <ul className="mt-1 space-y-0.5 ml-2">
                  <li>• Simple Q&A: 500-1,500</li>
                  <li>• Code gen: 1,500-4,000</li>
                  <li>• Doc analysis: 3,000-8,000</li>
                </ul>
              </div>
              <p className="text-sm mt-2">
                Your avg: <span className="font-bold text-lime-accent" style={{ textShadow: '0 0 10px #b2ff59' }}>
                  {data?.dailyRequests ? formatNumber(Math.round(tokenMetrics.totalTokens24h / data.dailyRequests)) : '--'} tokens/req
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Data Source Footer */}
      <div className="mt-6 relative z-10">
        <div className="bg-dark-card border border-dark-border rounded-lg p-4 flex items-center justify-between"
             style={{ background: '#141414' }}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-lime-accent/10 flex items-center justify-center border border-lime-accent/30">
              <CreditCard size={16} className="text-lime-accent" />
            </div>
            <div>
              <p className="text-sm font-medium text-white">Data Source</p>
              <p className="text-xs text-gray-500">{data?.dataSource?.replace(/_/g, ' ').replace(/and/g, '&') || 'AWS Cost Explorer & CloudWatch'}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-500">Last synced</p>
            <p className="text-sm font-medium text-white">
              {data?.timestamp ? new Date(data.timestamp).toLocaleString() : '--'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Financials;
